import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Checkbox,
  Flex,
  Form,
  InputNumber,
  Popover,
  Spin,
  Tag
} from 'antd'
import { useEffect, useState } from 'react'

import {
  useStocksServiceGetInternalInventoryListEndpointKey,
  useStocksServiceGetInventoryItemSettingsEndpoint,
  useStocksServiceGetInventoryListEndpointKey,
  useStocksServiceUpdateInventoryItemSettingsEndpoint
} from 'src/api/queries'
import { ApiError, InventoryStatus } from 'src/api/requests'
import { ArrowDown } from 'src/assets'
import { SaveConfirm } from 'src/components'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { getStockValidStatus } from '../utils'

type Props = {
  id?: string
  status?: InventoryStatus
}

export const ChangeStatusPopover = ({ id, status }: Props) => {
  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  const [popoverOpenState, setPopoverOpenState] = useState<
    Record<string, boolean>
  >({})

  const { t } = useLocales()

  const { data, isLoading } = useStocksServiceGetInventoryItemSettingsEndpoint(
    {
      id: String(id)
    },
    [String(id)],
    {
      enabled: !!id
    }
  )

  const { mutate: changeStatus } =
    useStocksServiceUpdateInventoryItemSettingsEndpoint({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [useStocksServiceGetInventoryListEndpointKey]
        })
        queryClient.invalidateQueries({
          queryKey: [useStocksServiceGetInternalInventoryListEndpointKey]
        })
        setPopoverOpenState((prev) => ({
          ...prev,
          [String(id)]: false
        }))
      },
      onError: (e: ApiError) => form.setFields(formErrorHandler(e.body.errors))
    })

  const onChangeStatus = async () => {
    const values = await form.validateFields()

    changeStatus({
      id: String(id),
      requestBody: {
        issuanceAllowed: values?.issuanceAllowed,
        quantityToNotify: Number(values?.quantityToNotify)
      }
    })
  }

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        issuanceAllowed: data?.issuanceAllowed,
        quantityToNotify: data?.quantityToNotify
      })
    }
  }, [data])

  return (
    <Popover
      trigger={['click']}
      open={popoverOpenState[String(id)]}
      onOpenChange={() =>
        setPopoverOpenState((prev) => ({
          ...prev,
          [String(id)]: !prev[String(id)]
        }))
      }
      title={t('stock.consumable_settings')}
      content={
        <Form form={form} layout="vertical" requiredMark={false}>
          {isLoading ? (
            <Flex justify="center">
              <Spin />
            </Flex>
          ) : (
            <Flex vertical gap={16}>
              <Form.Item valuePropName="checked" name="issuanceAllowed">
                <Checkbox>{t('stock.issuance_allowed')}</Checkbox>
              </Form.Item>

              <Form.Item
                label={t('stock.notify_quantity')}
                name="quantityToNotify"
              >
                <InputNumber placeholder={t('stock.notify_quantity')} min={0} />
              </Form.Item>

              <SaveConfirm
                title={`${t('common.save')}?`}
                onConfirm={onChangeStatus}
              >
                <Button type="primary">{t('common.save')}</Button>
              </SaveConfirm>
            </Flex>
          )}
        </Form>
      }
    >
      <Tag color={getStockValidStatus(t, status).color} className="pointer">
        <Flex justify="center" align="center" gap={2}>
          {status ? getStockValidStatus(t, status).text : '-'}{' '}
          <ArrowDown width={12} />
        </Flex>
      </Tag>
    </Popover>
  )
}
